import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebaseConfig";
import { collection, query, where, getDocs, doc, addDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import {
  AlertCircle, ArrowRight, BookOpenCheck, CheckCircle2, ChevronDown, Copy,
  GraduationCap, LoaderCircle, Maximize2, Plus, Search, ShieldCheck, Sparkles, Trash2,
  UserPlus, UsersRound,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import JoinCodeDialog from "./components/JoinCodeDialog";
import { validUsername } from "./utils/pendingUsername";
import { saveManagedStudentName } from "./utils/profileName";
import "./ViewStudents.css";
import { API_BASE_URL } from "./utils/apiBase";

const emptyCreateStudentForm = {
  firstName: "",
  lastName: "",
  email: "",
  classId: "",
};

const MAX_BULK_STUDENTS = 50;

const ViewStudents = ({ user }) => {
  const navigate = useNavigate();
  const currentTeacherUid = String(auth.currentUser?.uid || user?.uid || user?.id || "").trim();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classMemberships, setClassMemberships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("displayName");
  const [sortDirection, setSortDirection] = useState("asc");
  const teacherCode = user?.teacherCode || "";
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    // Use explicit string comparison to avoid truthy false values
    const savedMode = localStorage.getItem("darkMode");
    return savedMode === "true" ? true : false;
  });
  const [selectedClassForAssign, setSelectedClassForAssign] = useState({});
  const [assignmentMessage, setAssignmentMessage] = useState(null);
  const [createClassName, setCreateClassName] = useState("");
  const [createClassCode, setCreateClassCode] = useState("");
  const [createMessage, setCreateMessage] = useState(null);
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [expandedClasses, setExpandedClasses] = useState({});
  const [expandedClassId, setExpandedClassId] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [resetPasswordDialog, setResetPasswordDialog] = useState({
    status: "closed",
    student: null,
    temporaryPassword: "",
    error: "",
    isSubmitting: false,
  });
  const [copyMessage, setCopyMessage] = useState("");
  const [editNameDialog, setEditNameDialog] = useState({
    status: "closed",
    student: null,
    name: "",
    error: "",
    isSubmitting: false,
  });
  const savingNameRef = useRef(false);
  const [addStudentDialog, setAddStudentDialog] = useState({
    status: "closed",
    student: null,
    temporaryPassword: "",
    error: "",
    isSubmitting: false,
  });
  const [createStudentForm, setCreateStudentForm] = useState(emptyCreateStudentForm);
  const [createStudentCopyMessage, setCreateStudentCopyMessage] = useState("");
  const [bulkImport, setBulkImport] = useState({
    fileName: "",
    rows: [],
    error: "",
    isSubmitting: false,
    results: [],
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [copiedClassCodeId, setCopiedClassCodeId] = useState(null);
  // { kind: "teacher" | "class", code, className } while the large code view is open.
  const [codeDialog, setCodeDialog] = useState(null);
  const creditsRemaining = user?.creditsRemaining ?? null;

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToPageTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    };

    scrollToPageTop();
    const animationFrameId = window.requestAnimationFrame(scrollToPageTop);
    const timeoutId = window.setTimeout(scrollToPageTop, 0);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  // Check if user is logged in and is a teacher
  useEffect(() => {
    if (!user) {
      navigate("/");
    } else if (user.role !== "teacher") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Keep this page on the same theme state used by the shared dashboard shell.
  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch students using the shared authenticated teacher profile from App.
  useEffect(() => {
    const fetchTeacherCodeAndStudents = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!teacherCode) {
          throw new Error("Teacher code not found");
        }

        // Fetch students with that code in their teacherCodes array
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("role", "==", "student"),
          where("teacherCodes", "array-contains", teacherCode)
        );
        const querySnapshot = await getDocs(q);

        const studentsList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          studentsList.push({
            id: doc.id,
            uid: data.uid || doc.id,
            role: data.role || "student",
            displayName: data.displayName || data.username || "Unknown",
            username: data.username || "No username",
            email: data.email || "No email",
            createdAt: data.createdAt ? data.createdAt.toDate() : null,
            accountManagedByTeacher: data.accountManagedByTeacher === true,
            createdByTeacherId: data.createdByTeacherId || null,
            mustChangePassword: data.mustChangePassword === true,
          });
        });

        setStudents(studentsList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching students:", error);
        setError(error.message || "Error fetching students");
        setIsLoading(false);
      }
    };

    if (currentTeacherUid) {
      fetchTeacherCodeAndStudents();
    }
  }, [currentTeacherUid, teacherCode]);

  // Fetch classes for the teacher
  useEffect(() => {
    const fetchClasses = async () => {
      console.log("Current user uid:", currentTeacherUid);

      if (!currentTeacherUid) return;

      console.log("Fetching classes for teacherUid:", currentTeacherUid);

      try {
        const q = query(
          collection(db, "classes"),
          where("teacherUid", "==", currentTeacherUid),
          where("active", "==", true)
        );
        const snapshot = await getDocs(q);

        console.log("Classes snapshot size:", snapshot.size);

        const classList = snapshot.docs.map(doc => ({
          id: doc.id,
          className: doc.data().className,
          classCode: doc.data().classCode,
          ...doc.data()
        }));

        console.log("Classes loaded:", classList);

        setClasses(classList);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };

    fetchClasses();
  }, [currentTeacherUid]);

  // Fetch class memberships for the teacher
  useEffect(() => {
    const fetchClassMemberships = async () => {
      if (!currentTeacherUid) return;

      try {
        const q = query(
          collection(db, "classMemberships"),
          where("teacherUid", "==", currentTeacherUid)
        );
        const snapshot = await getDocs(q);

        console.log("Memberships snapshot size:", snapshot.size);

        const memberships = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log("Memberships loaded:", memberships);

        setClassMemberships(memberships);
      } catch (error) {
        console.error("Error fetching class memberships:", error);
      }
    };

    fetchClassMemberships();
  }, [currentTeacherUid]);

  // Sort students based on current sort field and direction
  const sortedStudents = [...students].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];

    // Handle date comparison - safely handle null values
    if (sortField === "createdAt") {
      // Treat null/undefined dates as 0 (earliest) for sorting
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    }

    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  // Sort handler function
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Format date for display - safely handles null/undefined dates
  const formatDate = (date) => {
    if (!date) {
      return "Unknown";
    }
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Helper function to get initials from a name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const canTeacherResetStudent = (student) => {
    const roleMatches = String(student?.role || "").trim().toLowerCase() === "student";
    const managedMatches = student?.accountManagedByTeacher === true;
    const ownerMatches = String(student?.createdByTeacherId || "").trim() === currentTeacherUid;

    return roleMatches && managedMatches && ownerMatches;
  };

  const openResetPasswordDialog = (student) => {
    if (!canTeacherResetStudent(student)) return;

    setCopyMessage("");
    setResetPasswordDialog({
      status: "confirm",
      student,
      temporaryPassword: "",
      error: "",
      isSubmitting: false,
    });
  };

  const closeResetPasswordDialog = () => {
    setCopyMessage("");
    setResetPasswordDialog({
      status: "closed",
      student: null,
      temporaryPassword: "",
      error: "",
      isSubmitting: false,
    });
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPasswordDialog.student || resetPasswordDialog.isSubmitting) return;

    setCopyMessage("");
    setResetPasswordDialog(prev => ({
      ...prev,
      error: "",
      isSubmitting: true,
    }));

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before resetting a password.");
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/api/students/${encodeURIComponent(resetPasswordDialog.student.id)}/reset-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok || !data.temporaryPassword) {
        throw new Error(data.error || "Could not reset the password. Please try again.");
      }

      setStudents(prev =>
        prev.map(student =>
          student.id === resetPasswordDialog.student.id
            ? { ...student, mustChangePassword: true }
            : student
        )
      );
      setResetPasswordDialog(prev => ({
        ...prev,
        status: "success",
        temporaryPassword: data.temporaryPassword,
        error: "",
        isSubmitting: false,
      }));
    } catch (err) {
      setResetPasswordDialog(prev => ({
        ...prev,
        error: err.message || "Could not reset the password. Please try again.",
        isSubmitting: false,
      }));
    }
  };

  const handleCopyTemporaryPassword = async () => {
    if (!resetPasswordDialog.temporaryPassword) return;

    try {
      await navigator.clipboard.writeText(resetPasswordDialog.temporaryPassword);
      setCopyMessage("Password copied.");
    } catch (err) {
      setCopyMessage("Copy failed. Select the password and copy it manually.");
    }
  };

  // The backend applies the same ownership check before saving.
  const openEditNameDialog = (student) => {
    if (!canTeacherResetStudent(student)) return;
    setEditNameDialog({
      status: "editing",
      student,
      name: student.displayName || student.username || "",
      error: "",
      isSubmitting: false,
    });
  };

  const closeEditNameDialog = () => {
    if (savingNameRef.current) return;
    setEditNameDialog({ status: "closed", student: null, name: "", error: "", isSubmitting: false });
  };

  const handleSaveStudentName = async (event) => {
    event.preventDefault();
    if (!editNameDialog.student || savingNameRef.current) return;

    const name = validUsername(editNameDialog.name);
    if (!name) {
      setEditNameDialog(prev => ({
        ...prev,
        error: prev.name.trim() ? "Names must be 100 characters or fewer." : "Please enter the student's name.",
      }));
      return;
    }

    const studentId = editNameDialog.student.id;
    savingNameRef.current = true;
    setEditNameDialog(prev => ({ ...prev, error: "", isSubmitting: true }));
    try {
      const savedName = (await saveManagedStudentName(studentId, name)) || name;
      setStudents(prev =>
        prev.map(student =>
          student.id === studentId ? { ...student, displayName: savedName, username: savedName } : student
        )
      );
      setClassMemberships(prev =>
        prev.map(membership =>
          membership.studentUid === studentId && membership.active
            ? {
                ...membership,
                studentName: savedName,
                ...("studentUsername" in membership ? { studentUsername: savedName } : {}),
              }
            : membership
        )
      );
      setEditNameDialog(prev => ({ ...prev, status: "success", name: savedName, isSubmitting: false }));
    } catch (err) {
      setEditNameDialog(prev => ({
        ...prev,
        error: err.message || "Could not save the name. Please try again.",
        isSubmitting: false,
      }));
    } finally {
      savingNameRef.current = false;
    }
  };

  const renderEditNameButton = (student) =>
    canTeacherResetStudent(student) ? (
      <button
        type="button"
        className="reset-password-button"
        onClick={() => openEditNameDialog(student)}
      >
        Edit Name
      </button>
    ) : null;

  const renderResetPasswordButton = (student) =>
    canTeacherResetStudent(student) ? (
      <button
        type="button"
        className="reset-password-button"
        onClick={() => openResetPasswordDialog(student)}
      >
        Reset Password
      </button>
    ) : null;

  const openAddStudentDialog = () => {
    setCreateStudentForm(emptyCreateStudentForm);
    setCreateStudentCopyMessage("");
    setBulkImport({ fileName: "", rows: [], error: "", isSubmitting: false, results: [] });
    setAddStudentDialog({
      status: "choice",
      student: null,
      temporaryPassword: "",
      error: "",
      isSubmitting: false,
    });
  };

  const closeAddStudentDialog = () => {
    setCreateStudentForm(emptyCreateStudentForm);
    setCreateStudentCopyMessage("");
    setBulkImport({ fileName: "", rows: [], error: "", isSubmitting: false, results: [] });
    setAddStudentDialog({
      status: "closed",
      student: null,
      temporaryPassword: "",
      error: "",
      isSubmitting: false,
    });
  };

  const showCreateOneStudentForm = () => {
    setCreateStudentCopyMessage("");
    setAddStudentDialog(prev => ({
      ...prev,
      status: "form",
      error: "",
      temporaryPassword: "",
      student: null,
    }));
  };

  const showBulkUploadFlow = () => {
    setCreateStudentCopyMessage("");
    setBulkImport({ fileName: "", rows: [], error: "", isSubmitting: false, results: [] });
    setAddStudentDialog(prev => ({
      ...prev,
      status: "bulk",
      error: "",
      temporaryPassword: "",
      student: null,
    }));
  };

  const updateCreateStudentField = (field, value) => {
    setCreateStudentForm(prev => ({
      ...prev,
      [field]: value,
    }));
    setAddStudentDialog(prev => ({
      ...prev,
      error: "",
    }));
  };

  const validateCreateStudentForm = () => {
    const firstName = createStudentForm.firstName.trim();
    const lastName = createStudentForm.lastName.trim();
    const email = createStudentForm.email.trim().toLowerCase();

    if (!firstName) return "Please enter the student's first name.";
    if (!lastName) return "Please enter the student's last name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Please enter a valid student email address.";
    }
    if (createStudentForm.classId && !classes.some(classItem => classItem.id === createStudentForm.classId)) {
      return "Please choose one of your classes, or leave Class blank.";
    }
    return "";
  };

  const handleCreateStudent = async () => {
    if (addStudentDialog.isSubmitting) return;

    const validationError = validateCreateStudentForm();
    if (validationError) {
      setAddStudentDialog(prev => ({ ...prev, error: validationError }));
      return;
    }

    setCreateStudentCopyMessage("");
    setAddStudentDialog(prev => ({
      ...prev,
      error: "",
      isSubmitting: true,
    }));

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before creating a student account.");
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/students/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: createStudentForm.firstName.trim(),
          lastName: createStudentForm.lastName.trim(),
          email: createStudentForm.email.trim().toLowerCase(),
          classId: createStudentForm.classId || "",
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok || !data.student || !data.temporaryPassword) {
        throw new Error(data.error || "Could not create the student account. Please try again.");
      }

      const createdStudent = {
        ...data.student,
        createdAt: new Date(),
      };
      setStudents(prev => [createdStudent, ...prev.filter(student => student.id !== createdStudent.id)]);

      if (data.classMembership) {
        setClassMemberships(prev => [
          ...prev.filter(membership => membership.id !== data.classMembership.id),
          {
            ...data.classMembership,
            joinedAt: new Date(),
          },
        ]);
      }

      setAddStudentDialog({
        status: "success",
        student: createdStudent,
        temporaryPassword: data.temporaryPassword,
        error: "",
        isSubmitting: false,
      });
    } catch (err) {
      setAddStudentDialog(prev => ({
        ...prev,
        error: err.message || "Could not create the student account. Please try again.",
        isSubmitting: false,
      }));
    }
  };

  const handleCopyCreatedStudentPassword = async () => {
    if (!addStudentDialog.temporaryPassword) return;

    try {
      await navigator.clipboard.writeText(addStudentDialog.temporaryPassword);
      setCreateStudentCopyMessage("Password copied.");
    } catch (err) {
      setCreateStudentCopyMessage("Copy failed. Select the password and copy it manually.");
    }
  };

  const handleCreateAnotherStudent = () => {
    setCreateStudentForm(emptyCreateStudentForm);
    setCreateStudentCopyMessage("");
    setAddStudentDialog({
      status: "form",
      student: null,
      temporaryPassword: "",
      error: "",
      isSubmitting: false,
    });
  };

  const downloadCsvFile = (filename, rows) => {
    const csv = Papa.unparse(rows, { header: true });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadBulkTemplate = () => {
    const blob = new Blob(["firstName,lastName,email,class\r\n"], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "automarker-student-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const normalizeCsvHeader = (value) => String(value || "").trim().toLowerCase();
  const normalizeCsvClassIdentifier = (value) => String(value || "").trim().toLowerCase();

  const resolveCsvClass = (classIdentifier) => {
    const normalizedClassIdentifier = normalizeCsvClassIdentifier(classIdentifier);
    if (!normalizedClassIdentifier) {
      return { classId: "", className: "" };
    }

    const matchesById = new Map();
    classes.forEach(classItem => {
      const normalizedClassName = normalizeCsvClassIdentifier(classItem.className);
      const normalizedClassCode = normalizeCsvClassIdentifier(classItem.classCode);
      if (
        normalizedClassName === normalizedClassIdentifier ||
        normalizedClassCode === normalizedClassIdentifier
      ) {
        matchesById.set(classItem.id, classItem);
      }
    });
    const matches = Array.from(matchesById.values());

    if (matches.length === 1) {
      return { classId: matches[0].id, className: matches[0].className };
    }

    if (matches.length > 1) {
      return { classId: "", className: classIdentifier, error: "Class is ambiguous." };
    }

    return { classId: "", className: classIdentifier, error: "Class not found." };
  };

  const previewBulkRowsWithBackend = async (rows, fileName) => {
    setBulkImport({ fileName, rows, error: "", isSubmitting: true, results: [] });

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before importing students.");
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/students/bulk-preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ students: rows }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Could not validate this CSV. Please try again.");
      }

      const localErrorsByRow = new Map(
        rows
          .filter(row => row.localError)
          .map(row => [row.rowNumber, row.localError])
      );
      const mergedRows = data.results.map(row => {
        const localError = localErrorsByRow.get(row.rowNumber);
        return localError
          ? { ...row, status: "invalid", error: localError }
          : row;
      });

      setBulkImport({
        fileName,
        rows: mergedRows,
        error: "",
        isSubmitting: false,
        results: [],
      });
    } catch (err) {
      setBulkImport({
        fileName,
        rows,
        error: err.message || "Could not validate this CSV. Please try again.",
        isSubmitting: false,
        results: [],
      });
    }
  };

  const handleBulkCsvSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setBulkImport({ fileName: file.name, rows: [], error: "Please upload a CSV file.", isSubmitting: false, results: [] });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: normalizeCsvHeader,
      complete: ({ data, errors, meta }) => {
        if (errors.length > 0) {
          setBulkImport({ fileName: file.name, rows: [], error: "This CSV could not be parsed. Please check the file format.", isSubmitting: false, results: [] });
          return;
        }

        const fields = (meta.fields || []).map(normalizeCsvHeader);
        const requiredFields = ["firstname", "lastname", "email"];
        const missingField = requiredFields.find(field => !fields.includes(field));
        if (missingField) {
          setBulkImport({ fileName: file.name, rows: [], error: "CSV must include firstName, lastName, and email columns.", isSubmitting: false, results: [] });
          return;
        }

        const studentRows = data
          .map((row, index) => {
            const firstName = String(row.firstname || "").trim();
            const lastName = String(row.lastname || "").trim();
            const email = String(row.email || "").trim().toLowerCase();
            const classValue = String(row.class || "").trim();
            const hasAnyValue = firstName || lastName || email || classValue;
            if (!hasAnyValue) return null;

            const resolvedClass = resolveCsvClass(classValue);
            const localErrors = [];
            if (resolvedClass.error) localErrors.push(resolvedClass.error);
            if (firstName.length > 80) localErrors.push("First name is too long.");
            if (lastName.length > 80) localErrors.push("Last name is too long.");
            if (email.length > 254) localErrors.push("Email is too long.");

            return {
              rowNumber: index + 2,
              firstName,
              lastName,
              email,
              className: resolvedClass.className || classValue,
              classId: resolvedClass.classId,
              localError: localErrors.join(" "),
            };
          })
          .filter(Boolean);

        if (studentRows.length === 0) {
          setBulkImport({ fileName: file.name, rows: [], error: "No student rows were found in this CSV.", isSubmitting: false, results: [] });
          return;
        }

        if (studentRows.length > MAX_BULK_STUDENTS) {
          setBulkImport({
            fileName: file.name,
            rows: [],
            error: "Bulk uploads are limited to 50 students at a time. Please split this file into smaller batches.",
            isSubmitting: false,
            results: [],
          });
          return;
        }

        previewBulkRowsWithBackend(studentRows, file.name);
      },
      error: () => {
        setBulkImport({ fileName: file.name, rows: [], error: "This CSV could not be parsed. Please check the file format.", isSubmitting: false, results: [] });
      },
    });
  };

  const handleCreateBulkStudents = async () => {
    const readyRows = bulkImport.rows.filter(row => row.status === "ready");
    if (readyRows.length === 0 || bulkImport.isSubmitting) return;

    setBulkImport(prev => ({ ...prev, error: "", isSubmitting: true, results: [] }));

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before creating student accounts.");
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/students/bulk-create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ students: readyRows }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok && !data.results) {
        throw new Error(data.error || "Could not create student accounts. Please try again.");
      }

      const createdResults = (data.results || []).filter(result => result.status === "created");
      const createdStudents = createdResults.map(result => ({
        ...result.student,
        createdAt: new Date(),
      }));
      const createdIds = new Set(createdStudents.map(student => student.id));
      if (createdStudents.length > 0) {
        setStudents(prev => [
          ...createdStudents,
          ...prev.filter(student => !createdIds.has(student.id)),
        ]);
      }

      const createdMemberships = createdResults
        .filter(result => result.classMembership)
        .map(result => ({ ...result.classMembership, joinedAt: new Date() }));
      if (createdMemberships.length > 0) {
        const createdMembershipIds = new Set(createdMemberships.map(membership => membership.id));
        setClassMemberships(prev => [
          ...prev.filter(membership => !createdMembershipIds.has(membership.id)),
          ...createdMemberships,
        ]);
      }

      setBulkImport(prev => ({
        ...prev,
        error: "",
        isSubmitting: false,
        results: data.results || [],
      }));
      setAddStudentDialog(prev => ({
        ...prev,
        status: "bulk-results",
        error: "",
      }));
    } catch (err) {
      setBulkImport(prev => ({
        ...prev,
        error: err.message || "Could not create student accounts. Please try again.",
        isSubmitting: false,
      }));
    }
  };

  const handleDownloadBulkCredentials = () => {
    const createdRows = bulkImport.results
      .filter(result => result.status === "created")
      .map(result => ({
        firstName: result.firstName,
        lastName: result.lastName,
        email: result.email,
        temporaryPassword: result.temporaryPassword,
        class: result.className || "",
      }));

    if (createdRows.length > 0) {
      downloadCsvFile("automarker-student-login-details.csv", createdRows);
    }
  };

  // Handle removing student from a class (soft-remove)
  const handleRemoveFromClass = async (membership, className) => {
    if (!window.confirm(`Remove this student from ${className}?`)) {
      return;
    }

    try {
      // Update the membership document to soft-remove
      const membershipRef = doc(db, "classMemberships", membership.id);
      await updateDoc(membershipRef, {
        active: false,
        removedAt: serverTimestamp()
      });

      // Update local state
      setClassMemberships(prev =>
        prev.map(m =>
          m.id === membership.id
            ? { ...m, active: false, removedAt: new Date() }
            : m
        )
      );

      setAssignmentMessage({ type: "success", text: "Student removed successfully" });
      setTimeout(() => setAssignmentMessage(null), 3000);
    } catch (error) {
      console.error("Error removing student from class:", error);
      setAssignmentMessage({ type: "error", text: "Could not remove student from class. Please try again." });
      setTimeout(() => setAssignmentMessage(null), 3000);
    }
  };

  // Create helper structures for class grouping (only active memberships)
  const classMap = {};
  classMemberships.forEach(m => {
    if (m.active) { // Only include active memberships
      if (!classMap[m.classId]) {
        classMap[m.classId] = [];
      }
      classMap[m.classId].push(m);
    }
  });

  // Set of assigned student UIDs (only from active memberships pointing to active classes)
  // A student is only truly assigned if:
  // 1. They have an active membership, AND
  // 2. The class they're assigned to is active (exists in our classes list)
  const assignedStudentIds = new Set();
  let validMembershipsCount = 0;

  classMemberships.forEach(m => {
    if (m.active) {
      // Check if the class this membership points to is active
      const classExists = classes.some(c => c.id === m.classId);
      if (classExists) {
        // This is a valid membership pointing to an active class
        assignedStudentIds.add(m.studentUid);
        validMembershipsCount++;
      }
    }
  });

  // Log unassigned students calculation
  console.log(`[unassigned] teacher students total: ${students.length}`);
  console.log(`[unassigned] active classes: ${classes.length}`);
  console.log(`[unassigned] active valid memberships counted: ${validMembershipsCount}`);

  // Unassigned students
  const unassignedStudents = students.filter(
    s => !assignedStudentIds.has(s.id)
  );

  console.log(`[unassigned] unassigned students: ${unassignedStudents.length}`);

  const studentById = new Map(students.map(student => [student.id, student]));
  const classById = new Map(classes.map(classItem => [classItem.id, classItem]));
  const searchableStudentsMap = new Map(students.map(student => [
    student.id,
    {
      ...student,
      classes: []
    }
  ]));

  classMemberships.forEach(membership => {
    if (!membership.active) return;

    const classInfo = classById.get(membership.classId);
    if (!classInfo) return;

    const studentInfo = studentById.get(membership.studentUid) || {};
    const existingStudent = searchableStudentsMap.get(membership.studentUid) || {
      id: membership.studentUid,
      role: studentInfo.role || "student",
      displayName: studentInfo.displayName || membership.studentName || membership.studentUsername || "Unknown Student",
      username: studentInfo.username || membership.studentUsername || "",
      email: studentInfo.email || membership.studentEmail || "",
      accountManagedByTeacher: studentInfo.accountManagedByTeacher === true,
      createdByTeacherId: studentInfo.createdByTeacherId || null,
      mustChangePassword: studentInfo.mustChangePassword === true,
      classes: []
    };

    if (!existingStudent.classes.some(classItem => classItem.id === classInfo.id)) {
      existingStudent.classes.push({
        id: classInfo.id,
        className: classInfo.className,
        classCode: classInfo.classCode
      });
    }

    searchableStudentsMap.set(membership.studentUid, existingStudent);
  });

  const normalizedStudentSearch = studentSearchTerm.trim().toLowerCase();
  const studentSearchResults = normalizedStudentSearch
    ? Array.from(searchableStudentsMap.values()).filter(student =>
        [
          student.displayName,
          student.username,
          student.email
        ]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(normalizedStudentSearch))
      )
    : [];

  // Generate class code from class name
  const generateClassCode = (name) => {
    return "CLS-" + name.trim().toUpperCase().replace(/\s+/g, "-").replace(/[^A-Z0-9-]/g, "");
  };

  // Handle deleting a class (soft delete - sets active to false)
  const handleDeleteClass = async (classItem) => {
    if (!window.confirm(`Delete "${classItem.className}"? Students will not be removed.`)) return;
    try {
       console.log(`[class delete] Deleting class: ${classItem.id} ${classItem.className}`);

       // Step 1: Find and deactivate all active memberships for this class
       const membershipsQuery = query(
         collection(db, "classMemberships"),
         where("teacherUid", "==", currentTeacherUid),
         where("classId", "==", classItem.id),
         where("active", "==", true)
       );
       const membershipsSnapshot = await getDocs(membershipsQuery);

       let deactivatedCount = 0;

       // Deactivate each membership
       for (const membershipDoc of membershipsSnapshot.docs) {
         const membershipRef = doc(db, "classMemberships", membershipDoc.id);
         await updateDoc(membershipRef, {
           active: false,
           removedAt: serverTimestamp(),
           removedReason: "class_deleted"
         });
         deactivatedCount++;
       }

       console.log(`[class delete] Deactivated ${deactivatedCount} memberships for class: ${classItem.id}`);

       // Step 2: Soft-delete the class itself
       const classRef = doc(db, "classes", classItem.id);
       await updateDoc(classRef, { active: false });

       console.log(`[class delete] Class delete/soft-delete complete`);

       // Step 3: Update local state to remove the class and deactivate memberships
       setClasses(prev => prev.filter(c => c.id !== classItem.id));
       setClassMemberships(prev =>
         prev.map(m =>
           m.classId === classItem.id && m.active
             ? { ...m, active: false, removedAt: new Date(), removedReason: "class_deleted" }
             : m
         )
       );

       if (expandedClassId === classItem.id) setExpandedClassId(null);
       setAssignmentMessage({ type: "success", text: `"${classItem.className}" deleted.` });
       setTimeout(() => setAssignmentMessage(null), 3000);
     } catch (err) {
       console.error("Error deleting class:", err);
       setAssignmentMessage({ type: "error", text: "Could not delete class." });
       setTimeout(() => setAssignmentMessage(null), 3000);
     }
   };

  // Handle creating a new class
  const handleCreateClass = async () => {
    if (!createClassName.trim()) {
      setCreateMessage({ type: "error", text: "Please enter a class name." });
      setTimeout(() => setCreateMessage(null), 3000);
      return;
    }
    setIsCreatingClass(true);

    console.log(`[VIEW STUDENTS] Creating class for teacher UID: ${currentTeacherUid}`);

    try {
      const finalCode = createClassCode.trim()
        ? createClassCode.trim().toUpperCase().replace(/\s+/g, "-").replace(/[^A-Z0-9-]/g, "")
        : generateClassCode(createClassName);

      console.log(`[VIEW STUDENTS] Requested class code: ${finalCode}`);

      // Check for duplicate class codes scoped to THIS teacher only
      const dupQ = query(
        collection(db, "classes"),
        where("teacherUid", "==", currentTeacherUid),
        where("classCode", "==", finalCode),
        where("active", "==", true)
      );
      const dupSnap = await getDocs(dupQ);

      console.log(`[VIEW STUDENTS] Existing classes with this code for teacher: ${dupSnap.size}`);
      console.log(`[VIEW STUDENTS] Class code blocked: ${dupSnap.size > 0}`);

      if (dupSnap.size > 0) {
        setCreateMessage({ type: "error", text: "You already have a class with this code. Please choose another." });
        setTimeout(() => setCreateMessage(null), 3000);
        return;
      }
      const docRef = await addDoc(collection(db, "classes"), {
        className: createClassName.trim(),
        classCode: finalCode,
        teacherUid: currentTeacherUid,
        teacherCode: teacherCode || "",
        active: true,
        createdAt: serverTimestamp()
      });

      console.log(`[VIEW STUDENTS] Class created successfully: ${docRef.id}`);

      setClasses(prev => [...prev, { id: docRef.id, className: createClassName.trim(), classCode: finalCode, teacherUid: currentTeacherUid, teacherCode: teacherCode || "", active: true, createdAt: new Date() }]);
      setCreateMessage({ type: "success", text: "Class created successfully." });
      setCreateClassName("");
      setCreateClassCode("");
      setTimeout(() => setCreateMessage(null), 3000);
    } catch (err) {
      console.error("Error creating class:", err);
      setCreateMessage({ type: "error", text: "Could not create class. Please try again." });
      setTimeout(() => setCreateMessage(null), 3000);
    } finally {
      setIsCreatingClass(false);
    }
  };

  // Handle assigning student to a class
  const handleAssignStudent = async (student, classToAssign) => {
    if (!classToAssign) return;

    try {
      // Check for duplicate active membership
      const existingQuery = query(
        collection(db, "classMemberships"),
        where("teacherUid", "==", currentTeacherUid),
        where("studentUid", "==", student.id),
        where("classId", "==", classToAssign.id),
        where("active", "==", true)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (existingSnapshot.size > 0) {
        setAssignmentMessage({ type: "info", text: "Student already in this class" });
        setTimeout(() => setAssignmentMessage(null), 3000);
        return;
      }

      // Create membership document
      const docRef = await addDoc(collection(db, "classMemberships"), {
        classId: classToAssign.id,
        classCode: classToAssign.classCode,
        className: classToAssign.className,
        teacherUid: currentTeacherUid,
        teacherCode: teacherCode || "",
        studentUid: student.id,
        studentName: student.displayName || student.username || student.email || "Unknown Student",
        studentUsername: student.username || "",
        studentEmail: student.email || "",
        active: true,
        joinedAt: serverTimestamp()
      });

      // Update local state with full membership object
      setClassMemberships(prev => [
        ...prev,
        {
          id: docRef.id,
          classId: classToAssign.id,
          classCode: classToAssign.classCode,
          className: classToAssign.className,
          teacherUid: currentTeacherUid,
          teacherCode: teacherCode || "",
          studentUid: student.id,
          studentName: student.displayName || student.username || student.email || "Unknown Student",
          studentUsername: student.username || "",
          studentEmail: student.email || "",
          active: true,
          joinedAt: new Date()
        }
      ]);

      // Show success message
      setAssignmentMessage({ type: "success", text: "Student assigned successfully" });

      // Clear the dropdown selection for this student
      setSelectedClassForAssign(prev => ({
        ...prev,
        [student.id]: ""
      }));

      // Auto-dismiss message
      setTimeout(() => setAssignmentMessage(null), 3000);
    } catch (error) {
      console.error("Error assigning student:", error);
      setAssignmentMessage({ type: "error", text: "Could not assign student. Please try again." });
      setTimeout(() => setAssignmentMessage(null), 3000);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!teacherCode) return;
    try {
      await navigator.clipboard.writeText(teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (copyError) {
      console.error("Could not copy teacher code", copyError);
    }
  };

  const handleCopyClassCode = async (classItem) => {
    if (!classItem.classCode) return;
    try {
      await navigator.clipboard.writeText(classItem.classCode);
      setCopiedClassCodeId(classItem.id);
      window.setTimeout(() => setCopiedClassCodeId(current => (current === classItem.id ? null : current)), 1800);
    } catch (copyError) {
      console.error("Could not copy class code", copyError);
    }
  };

  const showClassCode = (classItem) =>
    setCodeDialog({ kind: "class", code: classItem.classCode, className: classItem.className });

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const teacherDisplayName = accountDisplayName(user, "StudyDen teacher");
  const managedStudentsCount = students.filter(canTeacherResetStudent).length;

  return (
    <div className={`sd-dashboard sd-role-teacher sd-brand-theme view-students-page${darkMode ? " sd-dark dark-mode" : ""}`}>
      <DashboardSidebar
        role="teacher"
        username={teacherDisplayName}
        creditsRemaining={creditsRemaining}
        teacherCode={teacherCode}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/view-students"
        officialDashboardBrand
      />
      <div className="sd-workspace vs-workspace">
        <DashboardHeader
          username={teacherDisplayName}
          role="teacher"
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode(value => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={studentSearchTerm}
          onSearch={setStudentSearchTerm}
        />

        <main className="sd-main vs-main">
          <section className="vs-hero">
            <div className="vs-hero-copy">
              <p className="vs-eyebrow">Classes &amp; Students</p>
              <h1>Manage Classes &amp; Students</h1>
              <p>View your classes, organise connected students, and manage account access in one place.</p>
              <div className="vs-hero-actions">
                <button type="button" className="add-student-header-button" onClick={openAddStudentDialog}>
                  <UserPlus size={17} />Add Student
                </button>
                <button type="button" className="teacher-code-action" onClick={handleCopyTeacherCode} disabled={!teacherCode}>
                  <Copy size={16} />
                  <span><small>Teacher code</small>{copiedTeacherCode ? "Copied" : teacherCode || "Unavailable"}</span>
                </button>
                <button type="button" className="teacher-code-action" onClick={() => setCodeDialog({ kind: "teacher", code: teacherCode })}
                  disabled={!teacherCode} aria-label="Show teacher code in large view">
                  <Maximize2 size={16} />
                  <span>Show code</span>
                </button>
              </div>
            </div>
            <div className="vs-hero-art" aria-hidden="true">
              <span className="vs-hero-icon"><UsersRound size={38} /></span>
              <i className="vs-orbit vs-orbit-one"><GraduationCap size={18} /></i>
              <i className="vs-orbit vs-orbit-two"><Sparkles size={17} /></i>
              <i className="vs-orbit vs-orbit-three"><BookOpenCheck size={17} /></i>
            </div>
          </section>

          <section className="vs-metrics" aria-label="Class and student summary">
            <article className="vs-metric vs-tone-blue">
              <span><GraduationCap size={21} /></span><div><strong>{isLoading ? "—" : classes.length}</strong><p>Total Classes</p></div>
            </article>
            <article className="vs-metric vs-tone-teal">
              <span><UsersRound size={21} /></span><div><strong>{isLoading ? "—" : students.length}</strong><p>Connected Students</p></div>
            </article>
            <article className="vs-metric vs-tone-purple">
              <span><ShieldCheck size={21} /></span><div><strong>{isLoading ? "—" : managedStudentsCount}</strong><p>Managed Accounts</p></div>
            </article>
            <article className="vs-metric vs-tone-amber">
              <span><UserPlus size={21} /></span><div><strong>{isLoading ? "—" : unassignedStudents.length}</strong><p>Unassigned</p></div>
            </article>
          </section>

          <section className="vs-tool-grid">
            {!isLoading && !error && (
              <div className="student-search-card">
                <div className="vs-panel-heading">
                  <span className="vs-panel-icon vs-icon-teal"><Search size={19} /></span>
                  <div><h2>Search Students</h2><p>Find students connected to your account.</p></div>
                  {studentSearchTerm && <button type="button" className="student-search-clear" onClick={() => setStudentSearchTerm("")}>Clear</button>}
                </div>
                <label className="student-search-input-wrap">
                  <Search size={18} aria-hidden="true" />
                  <input type="search" className="student-search-input" value={studentSearchTerm}
                    onChange={(event) => setStudentSearchTerm(event.target.value)} placeholder="Search students..."
                    aria-label="Search students by name, username, or email" />
                </label>
                {normalizedStudentSearch && (
                  <div className="student-search-results">
                    {studentSearchResults.length ? studentSearchResults.map(result => (
                      <article key={result.id} className="student-search-result-card">
                        <span className="student-avatar">{getInitials(result.displayName)}</span>
                        <div className="student-search-result-main">
                          <h3 className="student-search-result-name">{result.displayName}</h3>
                          {result.email && result.email !== "No email" && <p className="student-search-result-meta">{result.email}</p>}
                          <div className="student-badge-row">
                            <span className={`student-account-badge ${canTeacherResetStudent(result) ? "managed" : "registered"}`}>
                              {canTeacherResetStudent(result) ? "Managed Account" : "Self Registered"}
                            </span>
                            <span className="student-search-result-classes">
                              {result.classes.length ? result.classes.map(classItem => classItem.className).join(", ") : "Unassigned"}
                            </span>
                          </div>
                        </div>
                        <div className="student-search-actions">
                          {renderEditNameButton(result)}
                          {renderResetPasswordButton(result)}
                          <button type="button" className="student-search-profile-button" onClick={() => navigate(`/student-profile/${result.id}`)}>
                            View Profile<ArrowRight size={15} />
                          </button>
                        </div>
                      </article>
                    )) : <div className="student-search-empty"><Search size={22} /><strong>No students match your search.</strong><span>Try another name, username, or email.</span></div>}
                  </div>
                )}
              </div>
            )}

            <div className="create-class-card">
              <div className="vs-panel-heading">
                <span className="vs-panel-icon vs-icon-purple"><Plus size={19} /></span>
                <div><h2>Create Class</h2><p>Create a class and share its code.</p></div>
              </div>
              <div className="create-class-form">
                <div className="create-class-inputs-row">
                  <div className="create-class-field">
                    <label className="create-class-label" htmlFor="create-class-name">Class name</label>
                    <input id="create-class-name" type="text" value={createClassName} onChange={(event) => setCreateClassName(event.target.value)} placeholder="e.g. Year 11 Computer Science" className="create-class-input" />
                  </div>
                  <div className="create-class-field">
                    <label className="create-class-label" htmlFor="create-class-code">Class code <span className="label-optional">(optional)</span></label>
                    <input id="create-class-code" type="text" value={createClassCode} onChange={(event) => setCreateClassCode(event.target.value)} placeholder="Auto-generated if blank" className="create-class-input" />
                  </div>
                </div>
                <div className="create-class-footer">
                  <div className="create-class-preview">
                    {createClassCode.trim() ? 'Code preview: ' + createClassCode.trim().toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '') : createClassName.trim() ? 'Code preview: ' + generateClassCode(createClassName) : 'Code preview will appear here.'}
                  </div>
                  <button type="button" onClick={handleCreateClass} disabled={isCreatingClass} className={`create-class-button ${isCreatingClass ? 'disabled' : ''}`}>
                    {isCreatingClass ? <><LoaderCircle className="vs-spinner" size={17} />Creating...</> : <><Plus size={17} />Create Class</>}
                  </button>
                </div>
              </div>
              {createMessage && <div className={`create-class-message create-class-${createMessage.type}`}>{createMessage.text}</div>}
            </div>
          </section>

          {!isLoading && classes.length > 0 && (
            <section className="my-classes-section">
              <div className="vs-section-heading">
                <div><p className="vs-eyebrow">Your teaching groups</p><h2>My Classes</h2></div>
                <span>{classes.length} class{classes.length !== 1 ? "es" : ""}</span>
              </div>
              <div className="classes-container">
                {classes.map((classItem, index) => {
                  const memberCount = classMap[classItem.id]?.length || 0;
                  const isActive = expandedClassId === classItem.id;
                  // The tile is a container, not a button: the expand toggle and the
                  // code actions are sibling buttons, so neither triggers the other.
                  return (
                    <div key={classItem.id}
                      className={`class-tile class-tile-tone-${index % 4}${isActive ? " class-tile-active" : ""}`}>
                      <button type="button" className="class-tile-toggle"
                        onClick={() => setExpandedClassId(previous => previous === classItem.id ? null : classItem.id)}
                        aria-expanded={isActive}>
                        <span className="class-tile-icon"><GraduationCap size={21} /></span>
                        <span className="class-name">{classItem.className}</span>
                        <span className="class-tile-count">{memberCount} student{memberCount !== 1 ? "s" : ""}</span>
                        <ChevronDown className="class-tile-chevron" size={18} />
                      </button>
                      {classItem.classCode && (
                        <div className="class-tile-code">
                          <span className="class-tile-code-label">Join code</span>
                          <span className="class-code">{classItem.classCode}</span>
                          <div className="class-tile-actions">
                            <button type="button" className="class-tile-action" onClick={() => handleCopyClassCode(classItem)}
                              aria-label={`Copy join code for ${classItem.className}`}>
                              <Copy size={14} />{copiedClassCodeId === classItem.id ? "Copied" : "Copy"}
                            </button>
                            <button type="button" className="class-tile-action" onClick={() => showClassCode(classItem)}
                              aria-label={`Show join code for ${classItem.className} in large view`}>
                              <Maximize2 size={14} />Show
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {expandedClassId && (() => {
                const classItem = classes.find(item => item.id === expandedClassId);
                if (!classItem) return null;
                const memberCount = classMap[classItem.id]?.length || 0;
                return (
                  <div className="class-expanded-panel">
                    <div className="class-expanded-header">
                      <div className="class-section-info">
                        <span className="vs-panel-icon vs-icon-blue"><GraduationCap size={19} /></span>
                        <div><h3 className="class-section-name">{classItem.className}</h3><span className="class-section-code">Join code: {classItem.classCode}</span></div>
                        <span className="class-section-badge">{memberCount} student{memberCount !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="class-expanded-actions">
                        {classItem.classCode && (
                          <button type="button" className="show-class-code-button" onClick={() => showClassCode(classItem)}>
                            <Maximize2 size={15} />Show join code
                          </button>
                        )}
                        <button type="button" className="delete-class-button" onClick={() => handleDeleteClass(classItem)}><Trash2 size={15} />Delete Class</button>
                      </div>
                    </div>
                    <div className="class-section-body">
                      {memberCount ? (
                        <div className="students-table">
                          <div className="students-table-header">
                            <div className="students-table-cell students-name-col">Student</div>
                            <div className="students-table-cell students-email-col">Account</div>
                            <div className="students-table-cell students-actions-col">Actions</div>
                          </div>
                          {classMap[classItem.id].map(membership => {
                            const rowStudent = studentById.get(membership.studentUid) || {
                              id: membership.studentUid, role: "student",
                              displayName: membership.studentName || membership.studentUsername || "Unknown",
                              username: membership.studentUsername || "", email: membership.studentEmail || "",
                            };
                            const studentName = membership.studentName || membership.studentUsername || "Unknown";
                            return (
                              <div key={membership.studentUid} className="students-table-row">
                                <div className="students-table-cell students-name-col">
                                  <span className="student-avatar">{getInitials(studentName)}</span>
                                  <div><button type="button" className="student-name-link" onClick={() => navigate(`/student-profile/${membership.studentUid}`)}>{studentName}</button>
                                    <small>{membership.studentEmail && membership.studentEmail !== "No email" ? membership.studentEmail : "No email available"}</small></div>
                                </div>
                                <div className="students-table-cell students-email-col">
                                  <span className={`student-account-badge ${canTeacherResetStudent(rowStudent) ? "managed" : "registered"}`}>
                                    {canTeacherResetStudent(rowStudent) ? "Managed Account" : "Self Registered"}
                                  </span>
                                </div>
                                <div className="students-table-cell students-actions-col">
                                  <div className="student-row-actions">
                                    <button type="button" className="student-profile-row-button" onClick={() => navigate(`/student-profile/${membership.studentUid}`)}>View Profile<ArrowRight size={14} /></button>
                                    {renderEditNameButton(rowStudent)}
                                    {renderResetPasswordButton(rowStudent)}
                                    <button type="button" className="remove-from-class-button" onClick={() => handleRemoveFromClass(membership, classItem.className)}>Remove</button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <div className="no-students"><UsersRound size={24} /><strong>No students in this class yet.</strong><span>Assign a connected student when you’re ready.</span></div>}
                    </div>
                  </div>
                );
              })()}
            </section>
          )}

          {isLoading && <div className="loading-container"><LoaderCircle className="vs-spinner" size={25} /><p>Loading classes and students...</p></div>}
          {error && !isLoading && <div className="error-message"><AlertCircle size={20} /><div><strong>We couldn’t load your students.</strong><span>{error}</span></div></div>}

          {!isLoading && !error && students.length === 0 && (
            <div className="empty-state"><span><UsersRound size={30} /></span><div><strong>No students have joined you yet.</strong><p className="empty-state-sub">Share your teacher code so students can connect to your account.</p></div></div>
          )}

          {!isLoading && !error && students.length > 0 && (
            <section className="students-grouped-container">
              <div className="vs-section-heading">
                <div><p className="vs-eyebrow">Student management</p><h2>Students</h2></div>
                <span>{students.length} connected</span>
              </div>
              {unassignedStudents.length > 0 ? (
                <div className="unassigned-students-card">
                  <button type="button" className="class-section-header unassigned-header"
                    onClick={() => setExpandedClasses(previous => ({ ...previous, unassigned: !previous.unassigned }))}
                    aria-expanded={Boolean(expandedClasses.unassigned)}>
                    <div className="class-section-info">
                      <span className="vs-panel-icon vs-icon-amber"><UserPlus size={19} /></span>
                      <div><h3 className="class-section-name">Unassigned Students</h3><span className="class-section-code">Connected students who are not currently in a class</span></div>
                      <span className="class-section-badge unassigned-badge">{unassignedStudents.length}</span>
                    </div>
                    <ChevronDown className={`class-section-chevron${expandedClasses.unassigned ? " expanded" : ""}`} size={19} />
                  </button>
                  {expandedClasses.unassigned && (
                    <div className="class-section-body">
                      {unassignedStudents.map(student => (
                        <article key={student.id} className="unassigned-student-row">
                          <span className="student-avatar">{getInitials(student.displayName)}</span>
                          <div className="unassigned-student-info">
                            <button type="button" className="unassigned-student-name student-name-link" onClick={() => navigate(`/student-profile/${student.id}`)}>{student.displayName}</button>
                            {student.email && student.email !== "No email" && <span className="unassigned-student-email">{student.email}</span>}
                            <span className={`student-account-badge ${canTeacherResetStudent(student) ? "managed" : "registered"}`}>
                              {canTeacherResetStudent(student) ? "Managed Account" : "Self Registered"}
                            </span>
                          </div>
                          <div className="unassigned-assign-controls">
                            <button type="button" className="student-profile-row-button" onClick={() => navigate(`/student-profile/${student.id}`)}>View Profile<ArrowRight size={14} /></button>
                            {renderEditNameButton(student)}
                            {renderResetPasswordButton(student)}
                            {classes.length === 1 ? (
                              <button type="button" className="assign-class-button" onClick={() => handleAssignStudent(student, classes[0])}>Assign to {classes[0].className}</button>
                            ) : classes.length > 1 ? (
                              <><select className="assign-class-select" value={selectedClassForAssign[student.id] || ""} onChange={(event) => setSelectedClassForAssign(previous => ({ ...previous, [student.id]: event.target.value }))}>
                                <option value="">Select a class</option>{classes.map(classItem => <option key={classItem.id} value={classItem.id}>{classItem.className}</option>)}
                              </select><button type="button" className="assign-class-button" onClick={() => { const classId = selectedClassForAssign[student.id]; if (classId) handleAssignStudent(student, classes.find(classItem => classItem.id === classId)); }} disabled={!selectedClassForAssign[student.id]}>Assign</button></>
                            ) : <span className="no-class-hint">Create a class to assign</span>}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              ) : <div className="student-search-empty"><CheckCircle2 size={22} /><strong>Everyone is assigned.</strong><span>All connected students currently belong to a class.</span></div>}
              {assignmentMessage && <div className={`assignment-message assignment-${assignmentMessage.type}`}>{assignmentMessage.text}</div>}
            </section>
          )}
        </main>
      </div>
      {addStudentDialog.status !== "closed" && (
        <div className="reset-password-modal-backdrop" role="presentation">
          <div className="reset-password-modal create-student-modal" role="dialog" aria-modal="true" aria-labelledby="add-student-title">
            {addStudentDialog.status === "choice" && (
              <>
                <h2 id="add-student-title" className="reset-password-modal-title">Add Student</h2>
                <div className="add-student-options">
                  <button type="button" className="add-student-option-button" onClick={showCreateOneStudentForm}>
                    <span className="add-student-option-title">Create One Student</span>
                    <span className="add-student-option-text">Create an email/password account with a temporary password.</span>
                  </button>
                  <button type="button" className="add-student-option-button" onClick={showBulkUploadFlow}>
                    <span className="add-student-option-title">Bulk Upload CSV</span>
                    <span className="add-student-option-text">Import up to 50 students from a CSV file.</span>
                  </button>
                </div>
                <div className="reset-password-modal-actions">
                  <button type="button" className="reset-password-cancel-button" onClick={closeAddStudentDialog}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {addStudentDialog.status === "form" && (
              <>
                <h2 id="add-student-title" className="reset-password-modal-title">Create Student</h2>
                <div className="create-student-form">
                  <div className="create-class-inputs-row">
                    <div className="create-class-field">
                      <label className="create-class-label" htmlFor="create-student-first-name">First Name</label>
                      <input
                        id="create-student-first-name"
                        type="text"
                        className="create-class-input"
                        value={createStudentForm.firstName}
                        onChange={(e) => updateCreateStudentField("firstName", e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="create-class-field">
                      <label className="create-class-label" htmlFor="create-student-last-name">Last Name</label>
                      <input
                        id="create-student-last-name"
                        type="text"
                        className="create-class-input"
                        value={createStudentForm.lastName}
                        onChange={(e) => updateCreateStudentField("lastName", e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="create-class-field">
                    <label className="create-class-label" htmlFor="create-student-email">Email</label>
                    <input
                      id="create-student-email"
                      type="email"
                      className="create-class-input"
                      value={createStudentForm.email}
                      onChange={(e) => updateCreateStudentField("email", e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="create-class-field">
                    <label className="create-class-label" htmlFor="create-student-class">Class <span className="label-optional">(optional)</span></label>
                    <select
                      id="create-student-class"
                      className="assign-class-select create-student-class-select"
                      value={createStudentForm.classId}
                      onChange={(e) => updateCreateStudentField("classId", e.target.value)}
                    >
                      <option value="">No class yet</option>
                      {classes.map(classItem => (
                        <option key={classItem.id} value={classItem.id}>{classItem.className}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {addStudentDialog.error && (
                  <div className="reset-password-error">{addStudentDialog.error}</div>
                )}
                <div className="reset-password-modal-actions">
                  <button
                    type="button"
                    className="reset-password-cancel-button"
                    onClick={closeAddStudentDialog}
                    disabled={addStudentDialog.isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="reset-password-confirm-button"
                    onClick={handleCreateStudent}
                    disabled={addStudentDialog.isSubmitting}
                  >
                    {addStudentDialog.isSubmitting ? "Creating..." : "Create Student"}
                  </button>
                </div>
              </>
            )}

            {addStudentDialog.status === "bulk" && (
              <>
                <h2 id="add-student-title" className="reset-password-modal-title">Bulk Upload CSV</h2>
                <p className="reset-password-modal-text">
                  Upload a CSV with firstName, lastName, email, and optional class columns. Accounts are created only after preview.
                </p>
                <div className="bulk-import-actions">
                  <button type="button" className="reset-password-cancel-button" onClick={handleDownloadBulkTemplate}>
                    Download CSV Template
                  </button>
                  <label className="bulk-upload-button">
                    Upload CSV
                    <input type="file" accept=".csv,text/csv" onChange={handleBulkCsvSelected} />
                  </label>
                </div>
                {bulkImport.fileName && <p className="bulk-file-name">{bulkImport.fileName}</p>}
                {bulkImport.error && <div className="reset-password-error">{bulkImport.error}</div>}
                {bulkImport.isSubmitting && <p className="bulk-file-name">Validating CSV...</p>}
                {bulkImport.rows.length > 0 && (
                  <div className="bulk-preview">
                    <div className="bulk-preview-summary">
                      <strong>{bulkImport.rows.filter(row => row.status === "ready").length} students ready to create</strong>
                      <strong>{bulkImport.rows.filter(row => row.status !== "ready").length} rows need attention</strong>
                    </div>
                    <div className="bulk-preview-table">
                      <div className="bulk-preview-row header">
                        <span>Row</span>
                        <span>Student</span>
                        <span>Email</span>
                        <span>Class</span>
                        <span>Status</span>
                      </div>
                      {bulkImport.rows.map(row => (
                        <div key={`${row.rowNumber}-${row.email}`} className={`bulk-preview-row ${row.status === "ready" ? "ready" : "invalid"}`}>
                          <span>{row.rowNumber}</span>
                          <span>{`${row.firstName} ${row.lastName}`.trim() || "Missing name"}</span>
                          <span>{row.email || "Missing email"}</span>
                          <span>{row.className || "Unassigned"}</span>
                          <span>{row.status === "ready" ? "Ready" : row.error}</span>
                        </div>
                      ))}
                    </div>
                    <p className="reset-password-modal-note">Invalid rows will be excluded. Review the preview before creating accounts.</p>
                  </div>
                )}
                <div className="reset-password-modal-actions">
                  <button
                    type="button"
                    className="reset-password-cancel-button"
                    onClick={closeAddStudentDialog}
                    disabled={bulkImport.isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="reset-password-confirm-button"
                    onClick={handleCreateBulkStudents}
                    disabled={bulkImport.isSubmitting || bulkImport.rows.filter(row => row.status === "ready").length === 0}
                  >
                    {bulkImport.isSubmitting
                      ? "Working..."
                      : `Create ${bulkImport.rows.filter(row => row.status === "ready").length} Student Account${bulkImport.rows.filter(row => row.status === "ready").length === 1 ? "" : "s"}`}
                  </button>
                </div>
              </>
            )}

            {addStudentDialog.status === "bulk-results" && (
              <>
                <h2 id="add-student-title" className="reset-password-modal-title">Student Accounts Created</h2>
                <div className="bulk-preview-summary">
                  <strong>{bulkImport.results.filter(result => result.status === "created").length} created successfully</strong>
                  <strong>{bulkImport.results.filter(result => result.status === "failed").length} failed</strong>
                </div>
                <p className="reset-password-modal-note">Download or copy these login details now. Temporary passwords cannot be viewed again after you close this screen.</p>
                <div className="bulk-preview-table results">
                  <div className="bulk-preview-row header">
                    <span>Row</span>
                    <span>Student</span>
                    <span>Email</span>
                    <span>Class</span>
                    <span>Status</span>
                  </div>
                  {bulkImport.results.map(result => (
                    <div key={`${result.rowNumber}-${result.email}`} className={`bulk-preview-row ${result.status === "created" ? "ready" : "invalid"}`}>
                      <span>{result.rowNumber}</span>
                      <span>{`${result.firstName} ${result.lastName}`.trim()}</span>
                      <span>{result.email}</span>
                      <span>{result.className || "Unassigned"}</span>
                      <span>
                        {result.status === "created"
                          ? result.temporaryPassword
                          : result.error || "Failed"}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="reset-password-modal-actions">
                  <button
                    type="button"
                    className="reset-password-cancel-button"
                    onClick={handleDownloadBulkCredentials}
                    disabled={bulkImport.results.filter(result => result.status === "created").length === 0}
                  >
                    Download Login Details CSV
                  </button>
                  <button type="button" className="reset-password-close-button" onClick={closeAddStudentDialog}>
                    Done
                  </button>
                </div>
              </>
            )}

            {addStudentDialog.status === "success" && (
              <>
                <h2 id="add-student-title" className="reset-password-modal-title">Student Created Successfully</h2>
                <div className="created-student-summary">
                  <p><strong>Name:</strong> {addStudentDialog.student?.displayName}</p>
                  <p><strong>Email:</strong> {addStudentDialog.student?.email}</p>
                </div>
                <div className="temporary-password-box">
                  <code>{addStudentDialog.temporaryPassword}</code>
                  <button type="button" className="copy-password-button" onClick={handleCopyCreatedStudentPassword}>
                    Copy Password
                  </button>
                </div>
                {createStudentCopyMessage && <p className="copy-password-message">{createStudentCopyMessage}</p>}
                <p className="reset-password-modal-note">Copy this password now. For security, StudyDen will not display it again.</p>
                <div className="reset-password-modal-actions">
                  <button type="button" className="reset-password-cancel-button" onClick={handleCreateAnotherStudent}>
                    Create Another Student
                  </button>
                  <button type="button" className="reset-password-close-button" onClick={closeAddStudentDialog}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {resetPasswordDialog.status !== "closed" && (
        <div className="reset-password-modal-backdrop" role="presentation">
          <div className="reset-password-modal" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
            {resetPasswordDialog.status === "success" ? (
              <>
                <h2 id="reset-password-title" className="reset-password-modal-title">Temporary Password</h2>
                <p className="reset-password-modal-text">
                  Share this password with {resetPasswordDialog.student?.displayName || "the student"}. They will be required to change it when they next sign in.
                </p>
                <div className="temporary-password-box">
                  <code>{resetPasswordDialog.temporaryPassword}</code>
                  <button type="button" className="copy-password-button" onClick={handleCopyTemporaryPassword}>
                    Copy Password
                  </button>
                </div>
                {copyMessage && <p className="copy-password-message">{copyMessage}</p>}
                <p className="reset-password-modal-note">This password is shown only once and is not stored in the app.</p>
                <div className="reset-password-modal-actions">
                  <button type="button" className="reset-password-close-button" onClick={closeResetPasswordDialog}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id="reset-password-title" className="reset-password-modal-title">Reset Student Password</h2>
                <p className="reset-password-modal-text">
                  Reset the password for {resetPasswordDialog.student?.displayName || "this student"} and require a password change at next sign-in?
                </p>
                {resetPasswordDialog.error && (
                  <div className="reset-password-error">{resetPasswordDialog.error}</div>
                )}
                <div className="reset-password-modal-actions">
                  <button
                    type="button"
                    className="reset-password-cancel-button"
                    onClick={closeResetPasswordDialog}
                    disabled={resetPasswordDialog.isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="reset-password-confirm-button"
                    onClick={handleConfirmResetPassword}
                    disabled={resetPasswordDialog.isSubmitting}
                  >
                    {resetPasswordDialog.isSubmitting ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editNameDialog.status !== "closed" && (
        <div className="reset-password-modal-backdrop" role="presentation">
          <div className="reset-password-modal" role="dialog" aria-modal="true" aria-labelledby="edit-student-name-title">
            <h2 id="edit-student-name-title" className="reset-password-modal-title">Edit Student Name</h2>
            {editNameDialog.status === "success" ? (
              <>
                <p className="reset-password-modal-text" role="status">
                  The name has been updated to {editNameDialog.name}.
                </p>
                <div className="reset-password-modal-actions">
                  <button type="button" className="reset-password-close-button" onClick={closeEditNameDialog}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSaveStudentName} noValidate>
                <p className="reset-password-modal-text">
                  Correct the name shown for this managed account. Their sign-in email and password stay the same.
                </p>
                <div className="create-student-form">
                  <div className="create-class-field">
                    <label className="create-class-label" htmlFor="edit-student-name">Name</label>
                    <input
                      id="edit-student-name"
                      type="text"
                      className="create-class-input"
                      value={editNameDialog.name}
                      onChange={(event) => {
                        const { value } = event.target;
                        setEditNameDialog(prev => ({ ...prev, name: value }));
                      }}
                      maxLength={100}
                      readOnly={editNameDialog.isSubmitting}
                    />
                  </div>
                </div>
                {editNameDialog.error && (
                  <div className="reset-password-error" role="alert">{editNameDialog.error}</div>
                )}
                <div className="reset-password-modal-actions">
                  <button
                    type="button"
                    className="reset-password-cancel-button"
                    onClick={closeEditNameDialog}
                    disabled={editNameDialog.isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="reset-password-confirm-button"
                    disabled={editNameDialog.isSubmitting}
                  >
                    {editNameDialog.isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {codeDialog && (
        <JoinCodeDialog
          kind={codeDialog.kind}
          code={codeDialog.code}
          className={codeDialog.className}
          onClose={() => setCodeDialog(null)}
        />
      )}
    </div>
  );
};

export default ViewStudents;
